import {KeyboardEvent, useContext, useState} from 'react';
import {Button} from 'reactstrap';
import groupBy from 'lodash/groupBy';
import {FaCaretDown, FaCaretRight} from 'react-icons/fa';

import {ColorLegendContext} from 'src/context';
import {
  HorizontalFraction,
  MultiSelectList,
  NoDataPlaceholder,
  NodeDetailHeader,
  SearchInput,
  Tag,
} from 'src/components';
import {useTextFilteredList} from 'src/hooks';
import {RelationshipListItem} from 'src/components/RelationshipList/RelationshipListItem';
import {RelationshipRowData} from 'src/components/RelationshipList/types';

import {useSelectList} from '../GraphContext';

import {
  NodeData,
  Props,
  TypeContainerProps,
  TypeListProps,
} from './types';
import {getNodeIdentity, getNodeType} from './utils';

import './styles.css';

const NoRecords = () => (
  <NoDataPlaceholder>
    There are no more items to expand.
  </NoDataPlaceholder>
);

export const GraphViewNodeFilter = ({
  node,
  nodes,
  handleCancel,
  handleUpdate,
}: Props) => {
  const [selectedNodes, setSelectedNodes] = useState<Array<NodeData>>([]);
  const [unselectedNodes, setUnselectedNodes] = useState<Array<NodeData>>([]);
  const {getLegendEntry} = useContext(ColorLegendContext);
  const {selectList, selectDispatch} = useSelectList();
  const findFromSelectList = (nodeid: string) => {
    const found = selectList?.find((item) => {
      return item.id === nodeid;
    });
    return found ? found.selected : false;
  };

  const handleSelectionChange = (rowData: Array<RelationshipRowData>) => {
    const selectedIds = rowData.filter((r) => r.selected).map(({id}) => id);
    const selectedNodes = nodes.filter(({id}) => selectedIds.includes(id));
    const unselectedNodes = nodes.filter(({id}) => !selectedIds.includes(id));
    setSelectedNodes(selectedNodes);
    setUnselectedNodes(unselectedNodes);
    setList(list.map((l) => ({...l, selected: selectedIds.includes(l.id)})));
    setOptionsList(list.map((l) => ({id: l.id, selected: selectedIds.includes(l.id)})));
  };

  const relationshipList = nodes
      .map(({
        id,
        metadata: {
          identity,
          type,
        },
        shortName,
        text,
      }) => ({
        id,
        name: shortName || text || identity,
        color: getLegendEntry(type),
        relation: type,
        selected: findFromSelectList(id),
      }));

  const optionsSelectList = nodes
      .map(({
        id,
      }) => ({
        id,
        selected: findFromSelectList(id),
      }));
  const [list, setList] = useState(relationshipList);
  const [optionsList, setOptionsList] = useState(optionsSelectList);
  selectDispatch({type: 'setSelectList', payload: optionsList});

  const {filteredList, setFilterText} = useTextFilteredList(list, 'name');

  if (nodes.length === 0) {
    return (
      <NoRecords />
    );
  }


  const handleSearchTermChanged = ({currentTarget: {value}}: KeyboardEvent<HTMLInputElement>) => {
    setFilterText(value);
  };

  return (
    <div className="GraphViewNodeFilter" style={{height: '90vh'}}>
      <div style={{height: '15vh'}}>
        <NodeDetailHeader name={node.data.shortName || node.data.text || node.data.name || getNodeIdentity(node)} identity={node.data.identity} type={getNodeType(node)} />
        <SearchInput onKeyUp={handleSearchTermChanged}/>
      </div>
      <div style={{height: '70vh', overflowY: 'scroll'}}>
        <TypeContainer
          list={filteredList}
          onSelectedItemsChange={handleSelectionChange}
        />
      </div>
      <div style={{height: '5vh'}}>
        <div className="GraphViewNodeFilter__controls">
          <Button color="primary" onClick={(e) => {
            e.preventDefault();
            handleUpdate(selectedNodes, unselectedNodes);
            setSelectedNodes([]);
            setUnselectedNodes([]);
          }}>Update</Button>
          <Button color="secondary" onClick={(e) => {
            e.preventDefault();
            setSelectedNodes([]);
            setUnselectedNodes([]);
            handleCancel();
          }}>Cancel</Button>
        </div>
      </div>
    </div>
  );
};

export const TypeContainer = ({list, onSelectedItemsChange}: TypeContainerProps) => {
  const types = groupBy(list, 'relation');
  return (
    <>
      {Object.keys(types).map((type) => (
        <TypeList
          key={type}
          list={types[type]}
          type={type}
          onSelectedItemsChange={(rowData) => {
            const selectedIds = rowData.filter((r) => r.selected).map(({id}) => id);
            const newList = list.map((l) => (
              l.relation === type ? {
                ...l,
                selected: l.relation === type && selectedIds.includes(l.id),
              } : l),
            );
            onSelectedItemsChange(newList);
          }}
        />
      ))}
    </>
  );
};

export const TypeList = ({list, type, onSelectedItemsChange}: TypeListProps) => {
  const [collapse, setCollapse] = useState<boolean>(false);

  const totalCount = list.length;
  const selectedCount = list.filter(({selected}) => selected).length;

  const fgColor = list[0]?.color?.fgColor ?? 'black';
  const bgColor = list[0]?.color?.color ?? 'var(--color-secondary-light-gray)';

  return (
    <div key={type}>
      <div className="TypeList__header" style={{borderColor: bgColor}}>
        <span onClick={() => setCollapse(!collapse)}>
          {collapse ? (
          <FaCaretRight color={bgColor} />
        ) : (
          <FaCaretDown color={bgColor} />
        )}
        </span>
        <Tag
          label={type}
          backgroundColor={bgColor}
          color={fgColor}
        />
        <span className="TypeList__stats">
          <HorizontalFraction
            numerator={selectedCount}
            denominator={totalCount}
          />
        </span>
      </div>
      {!collapse && (
        <div key={type}>
          {list ? <MultiSelectList
            readOnly={false}
            maxColumnCount={1}
            options={list}
            onSelectionChanged={onSelectedItemsChange}
            renderOption={({id, name, relation, color}) => (
              <RelationshipListItem
                key={id}
                id={id}
                name={name}
                relation={relation}
                color={color}
                goToNode={() => {}}
              />
            )}
          /> : <NoRecords />}
        </div>
      )}
    </div>
  );
};
